import { useEffect, useState } from "react";
import { Check, X, Mic2, Users, ClipboardCheck, Edit3, Eye, AlertTriangle, MoreVertical, IndianRupee } from "lucide-react";
import { http, fmtDate, fmtDateTime, fmtRupee, errMsg } from "../api";
import AudioPlayer from "../components/AudioPlayer";
import OrderDetailModal, { StageBadge } from "../components/OrderDetailModal";
import AdminOverview from "./AdminOverview";
import AdminReports from "./AdminReports";
import AdminSettings from "./AdminSettings";
import AdminAgreements from "./AdminAgreements";
import AdminAccess from "./AdminAccess";
import PitchLinks from "./PitchLinks";
import ManageCommunication from "./ManageCommunication";
import AdminCreators from "./AdminCreators";

function SampleReview() {
  const [samples, setSamples] = useState([]);
  const [config, setConfig] = useState({ categories: [] });
  const [status, setStatus] = useState("PENDING");
  const [category, setCategory] = useState("");
  const [pricingSample, setPricingSample] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [reason, setReason] = useState("");
  const [notice, setNotice] = useState("");
  const load = () => http.get(`/admin/samples?status=${status}`).then(r => setSamples(r.data));
  useEffect(() => { http.get("/config").then(r => setConfig(r.data)); }, []);
  useEffect(() => { load(); setCategory(""); }, [status]);

  const act = async (id, action, extra = {}) => {
    try { await http.post(`/admin/samples/${id}/${action}`, extra); setNotice(`Sample ${action}d`); load(); }
    catch (e) { setNotice(errMsg(e)); }
    setRejectId(null); setReason("");
  };

  const REJECT_REASONS = ["Audio quality issue", "Background noise", "Clipping / distortion", "Incorrect category", "Incorrect language", "Duplicate sample", "Not marketplace ready"];

  return (
    <>
      <div className="page-head">
        <div><h1>Sample Review</h1><p>Review every uploaded voice sample individually.</p></div>
        <div className="chip-row">
          {["PENDING", "APPROVED", "ARCHIVED"].map(s => (
            <button key={s} className={`chip ${status === s ? "active" : ""}`} onClick={() => setStatus(s)} data-testid={`sample-tab-${s}`}>{s}</button>
          ))}
        </div>
      </div>
      {notice && <div className="notice success">{notice}</div>}
      {status === "APPROVED" && <div className="filter-strip"><label>Filter by use case</label><select value={category} onChange={e => setCategory(e.target.value)} data-testid="approved-sample-category-filter"><option value="">All use cases</option>{config.categories.map(item => <option key={item}>{item}</option>)}</select></div>}
      <div className="list-rows">
        {samples.filter(s => !category || (s.categories || [s.category]).includes(category)).map(s => (
          <div key={s.id} className="list-row" style={{ flexDirection: "column", alignItems: "stretch" }} data-testid={`sample-review-${s.id}`}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ width: 42, height: 42, background: "var(--primary-soft)", color: "var(--primary)", borderRadius: 10, display: "grid", placeItems: "center", flexShrink: 0 }}><Mic2 size={18} /></div>
              <div className="list-row-info">
                <h3>{s.title}</h3>
                <p>{s.artist_name} ({s.public_id}) · {s.language} · {(s.categories || [s.category]).join(", ")} · {s.gender} · {s.voice_age}</p>
                <div className="tag-row" style={{ marginTop: 6 }}>{s.ai_voice && <span className="badge ai" data-testid={`ai-badge-${s.id}`}>AI voice</span>}{s.characteristics?.map(c => <span className="badge" key={c}>{c}</span>)}</div>
                {s.description && <p style={{ marginTop: 6, fontStyle: "italic" }}>{s.description}</p>}
              </div>
              <AudioPlayer id={s.id} />
              {status === "APPROVED" && <><div className="price-stack" data-testid={`approved-sample-price-${s.id}`}><span>Client price</span><strong>{fmtRupee(s.marketplace_rate)}/h</strong></div><button className="ghost icon-button" onClick={() => setPricingSample(s)} title="Edit sample pricing" data-testid={`edit-sample-pricing-${s.id}`}><MoreVertical size={18} /></button></>}
              {status === "PENDING" && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="pill-action approve" onClick={() => act(s.id, "approve")} data-testid={`approve-sample-${s.id}`}><Check size={14} /> Approve</button>
                  <button className="pill-action reject" onClick={() => setRejectId(s.id)} data-testid={`reject-sample-${s.id}`}><X size={14} /> Reject</button>
                </div>
              )}
              {status !== "PENDING" && s.status !== "ARCHIVED" && (
                <button className="pill-action neutral" onClick={() => act(s.id, "archive")}>Archive</button>
              )}
              {s.status === "ARCHIVED" && <button className="pill-action neutral" onClick={() => act(s.id, "restore")}>Restore</button>}
            </div>
            {rejectId === s.id && (
              <div style={{ padding: 12, background: "var(--surface-2)", borderRadius: 8, marginTop: 8 }}>
                <div className="chip-row" style={{ marginBottom: 8 }}>
                  {REJECT_REASONS.map(r => <button key={r} className={`chip ${reason === r ? "active" : ""}`} onClick={() => setReason(r)}>{r}</button>)}
                </div>
                <textarea rows={2} placeholder="Optional additional note..." value={reason} onChange={e => setReason(e.target.value)} />
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <button className="pill-action reject" onClick={() => act(s.id, "reject", { reason })} disabled={!reason} data-testid={`confirm-reject-${s.id}`}>Confirm reject</button>
                  <button className="ghost" onClick={() => { setRejectId(null); setReason(""); }}>Cancel</button>
                </div>
              </div>
            )}
            {s.admin_note && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>Note: {s.admin_note}</div>}
          </div>
        ))}
        {samples.length === 0 && <div className="empty"><ClipboardCheck size={30} /><div style={{ fontWeight: 600, marginTop: 8 }}>No {status.toLowerCase()} samples</div></div>}
      </div>
      {pricingSample && <SamplePricingModal sample={pricingSample} onClose={() => setPricingSample(null)} onSaved={() => { setPricingSample(null); load(); setNotice("Sample pricing updated"); }} />}
    </>
  );
}

function SamplePricingModal({ sample, onClose, onSaved }) {
  const [artistPay, setArtistPay] = useState(sample.artist_base_rate || "");
  const [margin, setMargin] = useState(sample.admin_margin ?? Math.round((sample.artist_base_rate || 0) * 0.2));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const clientPrice = Number(artistPay || 0) + Number(margin || 0);
  const save = async () => {
    if (Number(artistPay) <= 0 || Number(margin) < 0) { setError("Enter a valid artist pay and margin."); return; }
    setBusy(true); setError("");
    try { await http.patch(`/admin/samples/${sample.id}/pricing`, { artist_base_rate: Number(artistPay), admin_margin: Number(margin) }); onSaved(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  return <div className="modal-backdrop" onClick={onClose} data-testid="sample-pricing-modal"><div className="modal" onClick={e => e.stopPropagation()}><button className="modal-close" onClick={onClose} data-testid="close-sample-pricing"><X size={18} /></button><h2>Voice-wise pricing</h2><p>{sample.title} · {sample.public_id}</p><div className="field-row"><div><label>Artist pay / finished hour (₹)</label><input type="number" min="1" value={artistPay} onChange={e => setArtistPay(e.target.value)} data-testid="sample-pricing-artist-pay" /></div><div><label>Admin margin / finished hour (₹)</label><input type="number" min="0" value={margin} onChange={e => setMargin(e.target.value)} data-testid="sample-pricing-admin-margin" /></div></div><div className="price-preview" data-testid="sample-pricing-client-total"><span>Client price / finished hour</span><strong>{fmtRupee(clientPrice)}</strong><small>Artist pay + admin margin</small></div>{error && <div className="notice error">{error}</div>}<button className="primary" onClick={save} disabled={busy} data-testid="save-sample-pricing"><IndianRupee size={15} /> {busy ? "Saving…" : "Save voice price"}</button></div></div>;
}

function ArtistEditModal({ artist, config, onClose, onSaved }) {
  const [form, setForm] = useState(artist);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true); setNotice("");
    const { marketplace_rate, ...profileFields } = form;
    try { await http.patch(`/admin/artists/${artist.id}`, profileFields); onSaved(); }
    catch (e) { setNotice(errMsg(e)); } finally { setBusy(false); }
  };
  const toggleArr = (key, v) => {
    const arr = new Set(form[key] || []);
    arr.has(v) ? arr.delete(v) : arr.add(v);
    setForm({ ...form, [key]: Array.from(arr) });
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} data-testid="artist-edit-modal">
        <div className="modal-head">
          <h2>Edit {artist.public_id} · {artist.name}</h2>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="field-row">
          <div><label>Display name</label><input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          <div><label>Voice age</label><select value={form.voice_age || ""} onChange={e => setForm({ ...form, voice_age: e.target.value })}>
            {(config.voice_ages || []).map(a => <option key={a}>{a}</option>)}
          </select></div>
        </div>
        <div><label>Default profile pay (₹ / finished hour)</label><input data-testid="edit-base-rate" type="number" value={form.base_rate || 0} onChange={e => setForm({ ...form, base_rate: parseFloat(e.target.value) || 0 })} /><div className="chars-counter">Voice-wise artist pay and client price are edited from Sample Review → Approved → three-dot menu.</div></div>
        <div className="field-row" style={{ marginTop: 12 }}>
          <div><label>Availability</label><select value={form.availability || ""} onChange={e => setForm({ ...form, availability: e.target.value })}><option>Available</option><option>Limited availability</option><option>Unavailable</option></select></div>
          <div><label>Profile status</label><select value={form.profile_status || ""} onChange={e => setForm({ ...form, profile_status: e.target.value })}><option>PENDING</option><option>APPROVED</option><option>REJECTED</option><option>SUSPENDED</option></select></div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" style={{ width: "auto" }} checked={!!form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} data-testid="featured-toggle" />
            Featured on marketplace
          </label>
        </div>
        <div style={{ marginTop: 12 }}>
          <label>Voice characteristics (max 4)</label>
          <div className="chip-row">
            {(config.characteristics || []).map(c => (
              <button key={c} type="button" className={`chip ${(form.characteristics || []).includes(c) ? "active" : ""}`} onClick={() => {
                const arr = new Set(form.characteristics || []);
                if (arr.has(c)) arr.delete(c); else if (arr.size < 4) arr.add(c);
                setForm({ ...form, characteristics: Array.from(arr) });
              }}>{c}</button>
            ))}
          </div>
        </div>
        {notice && <div className="notice error" style={{ marginTop: 12 }}>{notice}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button className="primary" onClick={save} disabled={busy} data-testid="save-artist-edit">{busy ? "Saving..." : "Save changes"}</button>
          <button className="ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ArtistDetailModal({ artist, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => { http.get(`/admin/artists/${artist.id}`).then(r => setDetail(r.data)); }, [artist.id]);
  if (!detail) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} data-testid="artist-detail-modal">
        <div className="modal-head">
          <h2>{artist.name} · {artist.public_id}</h2>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="section-title">CONFIDENTIAL DETAILS</div>
        <div className="kv"><span>Email</span><span>{detail.user?.email}</span></div>
        <div className="kv"><span>Phone</span><span>{detail.user?.phone || "—"}</span></div>
        <div className="kv"><span>DOB</span><span>{artist.dob || "—"}</span></div>
        <div className="kv"><span>City / State</span><span>{artist.city || "—"}, {artist.state || "—"}</span></div>
        <div className="kv"><span>Base rate (private)</span><span>{fmtRupee(artist.base_rate)}</span></div>
        <div className="kv"><span>Marketplace rate</span><span>{fmtRupee(artist.marketplace_rate)}</span></div>
        <div className="kv"><span>Admin margin</span><span style={{ color: "var(--primary)" }}>{fmtRupee((artist.marketplace_rate || 0) - (artist.base_rate || 0))}</span></div>
        {detail.payment && (
          <>
            <div className="section-title">PAYMENT DETAILS (ADMIN ONLY)</div>
            <div className="kv"><span>Account holder</span><span>{detail.payment.account_holder}</span></div>
            <div className="kv"><span>Bank</span><span>{detail.payment.bank_name}</span></div>
            <div className="kv"><span>Account no.</span><span>{detail.payment.account_number}</span></div>
            <div className="kv"><span>IFSC</span><span>{detail.payment.ifsc}</span></div>
            {detail.payment.upi_id && <div className="kv"><span>UPI</span><span>{detail.payment.upi_id}</span></div>}
          </>
        )}
        <div className="section-title">SAMPLES ({detail.samples.length})</div>
        {detail.samples.map(s => (
          <div key={s.id} className="list-row" style={{ padding: 10, marginBottom: 6 }}>
            <div className="list-row-info"><strong>{s.title}</strong><p>{s.language} · {s.category}</p></div>
            <span className={`badge ${s.status === "APPROVED" ? "success" : s.status === "REJECTED" ? "danger" : "warning"}`}>{s.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArtistsList({ config }) {
  const [artists, setArtists] = useState([]);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notice, setNotice] = useState("");
  const load = () => http.get(`/admin/artists${status ? `?status=${status}` : ""}`).then(r => setArtists(r.data));
  useEffect(() => { load(); }, [status]);
  const act = async (id, action) => {
    try { await http.post(`/admin/artists/${id}/${action}`); setNotice(`Artist ${action}d`); load(); }
    catch (e) { setNotice(errMsg(e)); }
  };
  return (
    <>
      <div className="page-head">
        <div><h1>Artists</h1><p>Approve, edit and manage every voice artist.</p></div>
        <div className="chip-row">
          <button className={`chip ${!status ? "active" : ""}`} onClick={() => setStatus("")}>All</button>
          {["PENDING", "APPROVED", "REJECTED", "SUSPENDED"].map(s => (
            <button key={s} className={`chip ${status === s ? "active" : ""}`} onClick={() => setStatus(s)}>{s}</button>
          ))}
        </div>
      </div>
      {notice && <div className="notice success">{notice}</div>}
      <div className="list-rows">
        {artists.map(a => (
          <div key={a.id} className="list-row" data-testid={`artist-row-${a.id}`}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: "var(--surface-2)", display: "grid", placeItems: "center", flexShrink: 0 }}><Users size={18} color="var(--muted)" /></div>
            <div className="list-row-info">
              <h3>{a.name} <span style={{ color: "var(--muted)", fontWeight: 500, fontSize: 13 }}>({a.public_id})</span></h3>
              <p>{a.gender} · {a.voice_age} · {(a.languages || []).slice(0, 3).join(", ") || "—"} · {fmtRupee(a.marketplace_rate)} / hour</p>
              <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 4 }}>
                Samples: <strong>{a.sample_counts?.approved || 0}</strong> approved, <strong>{a.sample_counts?.pending || 0}</strong> pending, <strong>{a.sample_counts?.rejected || 0}</strong> rejected · Joined {fmtDate(a.created_at)}
              </div>
            </div>
            <div className="list-row-actions">
              <span className={`badge ${a.profile_status === "APPROVED" ? "success" : a.profile_status === "REJECTED" || a.profile_status === "SUSPENDED" ? "danger" : "warning"}`}>{a.profile_status}</span>
              <button className="pill-action neutral" onClick={() => setDetail(a)} data-testid={`view-artist-${a.id}`}><Eye size={13} /></button>
              <button className="pill-action neutral" onClick={() => setEditing(a)} data-testid={`edit-artist-${a.id}`}><Edit3 size={13} /></button>
              {a.profile_status !== "APPROVED" && <button className="pill-action approve" onClick={() => act(a.id, "approve")} data-testid={`approve-artist-${a.id}`}><Check size={13} /></button>}
              {a.profile_status !== "SUSPENDED" && <button className="pill-action reject" onClick={() => act(a.id, "suspend")} data-testid={`suspend-artist-${a.id}`}><X size={13} /></button>}
            </div>
          </div>
        ))}
        {artists.length === 0 && <div className="empty">No artists in this view.</div>}
      </div>
      {editing && <ArtistEditModal artist={editing} config={config} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {detail && <ArtistDetailModal artist={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

function ClientsList() {
  const [clients, setClients] = useState([]);
  useEffect(() => { http.get("/admin/clients").then(r => setClients(r.data)); }, []);
  return (
    <>
      <div className="page-head"><div><h1>Clients</h1><p>Every registered client and their history.</p></div></div>
      <div className="list-rows">
        {clients.map(c => (
          <div key={c.id} className="list-row" data-testid={`client-row-${c.id}`}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: "var(--surface-2)", display: "grid", placeItems: "center" }}><Users size={18} color="var(--muted)" /></div>
            <div className="list-row-info">
              <h3>{c.name}</h3>
              <p>{c.email} · {c.company || "Individual"} · {c.phone || "—"}</p>
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 12.5 }}>
              <div><div style={{ color: "var(--muted)" }}>Orders</div><strong>{c.orders}</strong></div>
              <div><div style={{ color: "var(--muted)" }}>Completed</div><strong>{c.completed}</strong></div>
            </div>
          </div>
        ))}
        {clients.length === 0 && <div className="empty">No clients yet.</div>}
      </div>
    </>
  );
}

function AllOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const load = () => http.get("/orders").then(r => setOrders(r.data));
  useEffect(() => { load(); }, []);
  const filtered = orders.filter(o => {
    if (filter === "all") return true;
    if (filter === "deadline") return o.deadline_revision_status === "REQUESTED";
    return o.status === filter;
  });
  return (
    <>
      <div className="page-head"><div><h1>Orders</h1><p>All orders across the marketplace. Click any order for full detail.</p></div></div>
      <div className="chip-row" style={{ marginBottom: 14 }}>
        {[["all", "All"], ["PENDING_ARTIST_ACKNOWLEDGEMENT", "Awaiting ack"], ["IN_PROGRESS", "In progress"], ["DELIVERED", "Delivered"], ["REVISION_REQUESTED", "Revision"], ["deadline", "Deadline requests"], ["COMPLETED", "Completed"], ["DECLINED", "Declined"]].map(([k, l]) => (
          <button key={k} className={`chip ${filter === k ? "active" : ""}`} onClick={() => setFilter(k)} data-testid={`orders-filter-${k}`}>{l}</button>
        ))}
      </div>
      <div className="list-rows">
        {filtered.map(o => (
          <div key={o.id} className={`list-row clickable ${o.deadline_revision_status === "REQUESTED" ? "danger-border" : ""}`} onClick={() => setOpenId(o.id)} data-testid={`admin-order-${o.id}`}>
            <div className="list-row-info">
              <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>{o.order_code}</div>
              <h3>{o.project_title}</h3>
              <p><strong>{o.client_company || o.client_name}</strong> → <strong>{o.artist_name}</strong> · {o.language} · {o.estimated_hours}h · {fmtRupee(o.estimated_amount)}</p>
              {o.deadline_revision_status === "REQUESTED" && <div style={{ marginTop: 6, fontSize: 12, color: "var(--danger)", fontWeight: 600 }}><AlertTriangle size={12} style={{ display: "inline" }} /> Deadline revision pending</div>}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
              <StageBadge status={o.status} />
              <small style={{ color: "var(--muted)" }}>Due {fmtDate(o.deadline)}</small>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="empty">No orders here.</div>}
      </div>
      {openId && <OrderDetailModal orderId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </>
  );
}

function AuditLog() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { http.get("/admin/audit").then(r => setLogs(r.data)); }, []);
  return (
    <>
      <div className="page-head"><div><h1>Audit log</h1><p>Every action across the workspace.</p></div></div>
      <div className="list-rows">
        {logs.map(l => (
          <div key={l.id} className="list-row" data-testid={`audit-${l.id}`}>
            <div className="list-row-info">
              <strong>{l.action.replaceAll("_", " ")}</strong>
              <p>{l.actor_id?.slice(0, 8)} → {l.target_id?.slice(0, 8) || "—"}</p>
              {l.reason && <small style={{ color: "var(--muted)" }}>Reason: {l.reason}</small>}
            </div>
            <small style={{ color: "var(--muted)" }}>{fmtDateTime(l.created_at)}</small>
          </div>
        ))}
        {logs.length === 0 && <div className="empty">No audit entries yet.</div>}
      </div>
    </>
  );
}

export default function AdminDashboard({ page, setPage, user }) {
  const [config, setConfig] = useState({});
  useEffect(() => { http.get("/config").then(r => setConfig(r.data)); }, []);
  if (page === "dashboard") return <AdminOverview setPage={setPage} />;
  if (page === "samples") return <SampleReview />;
  if (page === "artists") return <ArtistsList config={config} />;
  if (page === "clients") return <ClientsList />;
  if (page === "orders") return <AllOrders />;
  if (page === "reports") return <AdminReports />;
  if (page === "settings") return <AdminSettings />;
  if (page === "agreements") return <AdminAgreements />;
  if (page === "access") return <AdminAccess user={user} />;
  if (page === "audit") return <AuditLog />;
  if (page === "pitch-links") return <PitchLinks />;
  if (page === "communications") return <ManageCommunication />;
  if (page === "creators") return <AdminCreators />;
  return null;
}
